import { useMemo, useState } from "react";
import { Search, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { formatCompact, formatDate } from "@/lib/format";

const PAGE_SIZE = 10;

const COLS = [
  { key: "ranking", label: "#" },
  { key: "title", label: "Title" },
  { key: "published_at", label: "Date" },
  { key: "duration", label: "Duration" },
  { key: "views", label: "Views" },
  { key: "likes", label: "Likes" },
  { key: "comments", label: "Comments" },
  { key: "engagement", label: "Engagement %" },
];

export default function VideosTable({ videos }) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("views");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = (videos || []).filter((v) =>
      (v.title || "").toLowerCase().includes(q.toLowerCase())
    );
    list.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return list;
  }, [videos, q, sortKey, sortDir]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <h2 className="text-2xl font-bold tracking-tight">Recent Videos</h2>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="videos-search"
            placeholder="Search by title..."
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
      </div>
      <div data-testid="videos-table" className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="w-16"></TableHead>
                {COLS.map((c) => (
                  <TableHead key={c.key}>
                    <button
                      onClick={() => toggleSort(c.key)}
                      className="flex items-center gap-1 text-xs uppercase tracking-wider font-mono hover:text-foreground"
                    >
                      {c.label} <ArrowUpDown className="w-3 h-3 opacity-50" />
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((v) => (
                <TableRow key={v.id} className="border-border hover:bg-secondary/40">
                  <TableCell>
                    <img src={v.thumbnail} alt={`Thumbnail of ${v.title}`} loading="lazy" className="w-20 h-12 object-cover rounded-md" />
                  </TableCell>
                  <TableCell className="tabular-nums font-mono text-muted-foreground">#{v.ranking}</TableCell>
                  <TableCell className="max-w-md">
                    <a href={`https://youtube.com/watch?v=${v.id}`} target="_blank" rel="noreferrer" className="line-clamp-2 hover:text-primary transition-colors">
                      {v.title}
                    </a>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{formatDate(v.published_at)}</TableCell>
                  <TableCell className="font-mono tabular-nums">{v.duration}</TableCell>
                  <TableCell className="font-mono tabular-nums">{formatCompact(v.views)}</TableCell>
                  <TableCell className="font-mono tabular-nums">{formatCompact(v.likes)}</TableCell>
                  <TableCell className="font-mono tabular-nums">{formatCompact(v.comments)}</TableCell>
                  <TableCell className="font-mono tabular-nums text-primary">{v.engagement}%</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-12">No videos found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="p-4 flex items-center justify-between border-t border-border">
          <div className="text-xs text-muted-foreground font-mono">
            Page {page} of {pages} · {filtered.length} videos
          </div>
          <div className="flex gap-2">
            <Button data-testid="videos-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button data-testid="videos-next" variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      </div>
    </section>
  );
}
